import { useSettings } from "../../tools";
import { useSession } from "@dvrt/web-phone-client";
import { useDebugSettings } from "conference/tools";
import DebugInfoPanel from "../../DebugInfoPanel";
import { Rnd, RndResizeCallback } from "react-rnd";
import { DraggableEventHandler } from "react-draggable";
import { sidebarWidth } from "../tools";
import { ItemRect } from "conference/types";
import { VideoStyled } from "./tools";
import MenuButton from "./MenuButton";
import { useSelfVideoDialogSettings } from "./context";
import { useRoomSettings } from "../context";
import { styled } from "@mui/material/styles";

const getDefaultContainerRect = (track: MediaStreamTrack): ItemRect => {
    const trackSettings = track.getSettings();
    const width = sidebarWidth;
    const aspectRatio =
        trackSettings.width && trackSettings.height ? trackSettings.width / trackSettings.height : 4 / 3;
    const height = width / aspectRatio;
    return {
        x: window.innerWidth - width,
        y: window.innerHeight - height,
        width,
        height,
    };
};

export const RndStyled = styled(Rnd)(({ theme }) => ({
    boxShadow: theme.shadows[10],
    "&:hover .menuButton": {
        opacity: 0.9,
    },
}));

export const SelfVideoDialog = () => {
    const debugSettings = useDebugSettings();
    const settings = useSelfVideoDialogSettings();
    const context = useSession();
    const track = context.localVideoTrack;

    if (!track) return null;

    const updateRect = (r: Partial<ItemRect>) => {
        settings.rect = {
            ...(settings.rect ?? getDefaultContainerRect(track)),
            ...r,
        };
    };

    const onResizeStop: RndResizeCallback = (e, dir, elementRef, delta, position) => {
        updateRect({
            ...position,
            width: elementRef.style.width,
            height: elementRef.style.height,
        });
    };

    const onDragStop: DraggableEventHandler = (e, data) => {
        updateRect({
            x: data.x,
            y: data.y,
        });
    };

    const resetAr = () => {
        const aspectRatio = track.getSettings().aspectRatio ?? 4 / 3;
        updateRect({
            height: parseInt(settings.rect.width as string) / aspectRatio,
        });
    };

    const storedRect = settings.rect;
    const rndRect =
        storedRect?.width != null && storedRect?.height != null ? storedRect : getDefaultContainerRect(track);

    return (
        <RndStyled
            size={rndRect}
            position={rndRect}
            onDragStop={onDragStop}
            onResizeStop={onResizeStop}
            bounds="window"
        >
            <VideoStyled track={track} />
            {debugSettings.enabled && debugSettings.showVideoInfo && <DebugInfoPanel track={track} showEffect />}
            <MenuButton onResetAr={resetAr} />
        </RndStyled>
    );
};

export const SelfVideoDialogWrapper = () => {
    const settings = useSettings();
    const roomSettings = useRoomSettings();
    const isSidebarOpened = roomSettings.sidebarState !== "hidden";
    const { alwaysFloat } = useSelfVideoDialogSettings();

    return (!isSidebarOpened || alwaysFloat) && settings.video ? <SelfVideoDialog /> : null;
};
